import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Send, Loader2, Copy, Check, RefreshCw, Mic, MicOff, 
  Download, Trash2, Edit3, Volume2, VolumeX, Sparkles,
  Code, Calculator, Globe, Image as ImageIcon, Zap,
  ChevronDown, Settings2, Wand2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const AI_MODELS = [
  { id: 'gemini-2.5-flash', name: 'JAF Flash', provider: 'JAF AI', speed: 'Fast', icon: Zap },
  { id: 'gemini-2.5-pro', name: 'JAF Pro', provider: 'JAF AI', speed: 'Powerful', icon: Sparkles },
  { id: 'gpt-4.1', name: 'JAF Balanced', provider: 'JAF AI', speed: 'Balanced', icon: Wand2 },
  { id: 'claude-4-sonnet', name: 'JAF Creative', provider: 'JAF AI', speed: 'Creative', icon: Sparkles },
];

const ChatInterface = ({ messages, onSendMessage, isLoading, onRegenerate, onDeleteMessage }) => {
  const [input, setInput] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedModel, setSelectedModel] = useState(AI_MODELS[0]);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showTools, setShowTools] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages, isLoading]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInput(transcript);
      };
      
      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Enter to send
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (input.trim() && !isLoading) {
          handleSubmit(e);
        }
      }
      // Escape to cancel editing
      if (e.key === 'Escape' && editingId) {
        setEditingId(null);
        setEditContent('');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [input, isLoading, editingId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSendMessage(input.trim(), selectedModel.id);
      setInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) return;
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      } else {
        const utterance = new SpeechSynthesisUtterance(text.replace(/[#*`]/g, ''));
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
        setIsSpeaking(true);
      }
    }
  };

  const exportConversation = () => {
    const content = messages.map(m => 
      `${m.role === 'user' ? 'You' : 'JAF AI'}: ${m.content}`
    ).join('\n\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jaf-ai-conversation-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const CodeBlock = ({ language, value }) => {
    return (
      <div className="relative group my-4 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between h-10 bg-zinc-800 px-4">
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4 text-zinc-400" />
            <span className="text-xs text-zinc-400 font-mono">{language || 'code'}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => copyToClipboard(value, `code-${value.substring(0, 20)}`)}
          >
            {copiedId === `code-${value.substring(0, 20)}` ? (
              <><Check className="h-3 w-3 mr-1" /> Copied!</>
            ) : (
              <><Copy className="h-3 w-3 mr-1" /> Copy code</>
            )}
          </Button>
        </div>
        <SyntaxHighlighter
          language={language || 'text'}
          style={oneDark}
          customStyle={{
            margin: 0,
            borderRadius: '0 0 8px 8px',
            fontSize: '13px',
          }}
          showLineNumbers={value.split('\n').length > 3}
        >
          {value}
        </SyntaxHighlighter>
      </div>
    );
  };

  const MessageActions = ({ message }) => (
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => copyToClipboard(message.content, message.id)}
            >
              {copiedId === message.id ? (
                <Check className="h-3.5 w-3.5 text-green-400" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-zinc-400" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy message</TooltipContent>
        </Tooltip>

        {message.role === 'assistant' && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => speakText(message.content)}
                >
                  {isSpeaking ? (
                    <VolumeX className="h-3.5 w-3.5 text-zinc-400" />
                  ) : (
                    <Volume2 className="h-3.5 w-3.5 text-zinc-400" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isSpeaking ? 'Stop' : 'Read aloud'}</TooltipContent>
            </Tooltip>

            {onRegenerate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onRegenerate(message.id)}
                    disabled={isLoading}
                  >
                    <RefreshCw className="h-3.5 w-3.5 text-zinc-400" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Regenerate response</TooltipContent>
              </Tooltip>
            )}
          </>
        )}
      </TooltipProvider>
    </div>
  );

  const QuickTools = () => (
    <div className="flex items-center gap-2 mb-3">
      <Badge variant="outline" className="cursor-pointer hover:bg-zinc-800 transition-colors" onClick={() => setInput(input + ' [search the web for latest info]')}>
        <Globe className="h-3 w-3 mr-1" /> Web Search
      </Badge>
      <Badge variant="outline" className="cursor-pointer hover:bg-zinc-800 transition-colors" onClick={() => setInput(input + ' [calculate: ]')}>
        <Calculator className="h-3 w-3 mr-1" /> Calculate
      </Badge>
      <Badge variant="outline" className="cursor-pointer hover:bg-zinc-800 transition-colors" onClick={() => setInput(input + ' [generate code for: ]')}>
        <Code className="h-3 w-3 mr-1" /> Code
      </Badge>
      <Badge variant="outline" className="cursor-pointer hover:bg-zinc-800 transition-colors" onClick={() => setInput(input + ' [analyze this image: ]')}>
        <ImageIcon className="h-3 w-3 mr-1" /> Analyze Image
      </Badge>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header with model selector */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 text-zinc-300 hover:text-white">
              <selectedModel.icon className="h-4 w-4 text-emerald-400" />
              {selectedModel.name}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 bg-zinc-900 border-zinc-800">
            <DropdownMenuLabel className="text-zinc-400">Select AI Model</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-zinc-800" />
            {AI_MODELS.map((model) => (
              <DropdownMenuItem
                key={model.id}
                onClick={() => setSelectedModel(model)}
                className={cn(
                  "flex items-center justify-between cursor-pointer",
                  selectedModel.id === model.id && "bg-zinc-800"
                )}
              >
                <div className="flex items-center gap-2">
                  <model.icon className="h-4 w-4 text-emerald-400" />
                  <div>
                    <p className="text-sm text-white">{model.name}</p>
                    <p className="text-xs text-zinc-500">{model.provider}</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs">{model.speed}</Badge>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {messages.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={exportConversation}
              className="text-zinc-400 hover:text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-400 via-cyan-500 to-purple-500 flex items-center justify-center mb-6 animate-pulse">
              <Sparkles className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">JAF AI</h2>
            <p className="text-zinc-400 max-w-md mb-2">
              The most powerful AI assistant with real-time web search, 
              code execution, voice input, and multi-model support.
            </p>
            <div className="flex items-center gap-2 text-xs text-zinc-500 mb-8">
              <Badge variant="outline"><Globe className="h-3 w-3 mr-1" /> Web Search</Badge>
              <Badge variant="outline"><Mic className="h-3 w-3 mr-1" /> Voice Input</Badge>
              <Badge variant="outline"><Code className="h-3 w-3 mr-1" /> Code Expert</Badge>
              <Badge variant="outline"><ImageIcon className="h-3 w-3 mr-1" /> Vision</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-lg">
              {[
                { text: 'What\'s the weather in Tokyo right now?', icon: Globe },
                { text: 'Write a Python web scraper', icon: Code },
                { text: 'Explain quantum computing simply', icon: Sparkles },
                { text: 'What\'s trending in tech news today?', icon: Zap }
              ].map((suggestion) => (
                <button
                  key={suggestion.text}
                  onClick={() => setInput(suggestion.text)}
                  className="flex items-center gap-3 p-4 text-left text-sm text-zinc-300 bg-zinc-900 hover:bg-zinc-800 rounded-xl border border-zinc-800 transition-all hover:border-zinc-700 hover:scale-[1.02]"
                >
                  <suggestion.icon className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                  <span>{suggestion.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6 max-w-4xl mx-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "group flex gap-4",
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.role === 'assistant' && (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                )}
                <div className="flex flex-col gap-1 max-w-[85%]">
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-3 shadow-lg",
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-emerald-600 to-cyan-600 text-white'
                        : 'bg-zinc-900 text-zinc-100 border border-zinc-800'
                    )}
                  >
                    {message.role === 'assistant' ? (
                      <div className="prose prose-invert prose-sm max-w-none">
                        <ReactMarkdown
                          components={{
                            code(props) {
                              const { children, className, node, ...rest } = props;
                              const match = /language-(\w+)/.exec(className || '');
                              const isInline = !match && !String(children).includes('\n');
                              return !isInline && match ? (
                                <CodeBlock language={match[1]} value={String(children).replace(/\n$/, '')} />
                              ) : (
                                <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-emerald-400 text-sm" {...rest}>
                                  {children}
                                </code>
                              );
                            },
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between px-1">
                    <span className="text-xs text-zinc-600">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </span>
                    <MessageActions message={message} />
                  </div>
                </div>
                {message.role === 'user' && (
                  <div className="w-9 h-9 rounded-xl bg-zinc-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-medium">You</span>
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-4">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div className="bg-zinc-900 rounded-2xl px-4 py-3 border border-zinc-800">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-zinc-400 text-sm">JAF AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-zinc-800 p-4 bg-zinc-950/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          {showTools && <QuickTools />}
          <form onSubmit={handleSubmit}>
            <div className="relative flex items-end gap-2">
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything... (Ctrl+Enter to send)"
                  className="min-h-[60px] max-h-[200px] pr-24 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-none rounded-xl focus:ring-emerald-500 focus:border-emerald-500 text-base"
                  rows={1}
                />
                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setShowTools(!showTools)}
                        >
                          <Settings2 className={cn("h-4 w-4", showTools ? "text-emerald-400" : "text-zinc-400")} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Quick tools</TooltipContent>
                    </Tooltip>

                    {'webkitSpeechRecognition' in window && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={toggleVoiceInput}
                          >
                            {isListening ? (
                              <MicOff className="h-4 w-4 text-red-400 animate-pulse" />
                            ) : (
                              <Mic className="h-4 w-4 text-zinc-400" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{isListening ? 'Stop listening' : 'Voice input'}</TooltipContent>
                      </Tooltip>
                    )}
                  </TooltipProvider>
                </div>
              </div>
              <Button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="h-[60px] w-[60px] bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 disabled:opacity-50 rounded-xl shadow-lg shadow-emerald-500/20"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </div>
          </form>
          <div className="flex items-center justify-between mt-2 px-1">
            <p className="text-xs text-zinc-600">
              JAF AI with {selectedModel.name} • Real-time web search enabled
            </p>
            <p className="text-xs text-zinc-600">
              Press <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-400">Ctrl</kbd> + <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-zinc-400">Enter</kbd> to send
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
